import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { product as CartesianProduct } from 'cartesian-product-generator';
import { VariantListTypes } from 'enums/VariantListTypes';
import { environment } from 'environments/environment';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { ProductTagFilterModel } from 'models/generated/Entities.GormCrm.ProductTagFilter';
import { ProductVariantModel } from 'models/generated/Entities.GormCrm.ProductVariant';
import { ProductVariantsOptionModel } from 'models/generated/Entities.GormCrm.ProductVariantsOption';
import { VariantListModel } from 'models/generated/Entities.GormCrm.VariantList';
import { VariantListOptionModel } from 'models/generated/Entities.GormCrm.VariantListOption';
import { TagModel } from 'models/generated/Models.TagModel';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class ProductService {

	private baseUrl = `${environment.gatewayUrl}/api/products`;

	constructor(private readonly http: HttpClient) {
	}

	getProduct(id: string): Promise<ProductModel> {
		return firstValueFrom(this.http.get<ProductModel>(`${this.baseUrl}/${id}`));
	}

	createProduct(product: ProductModel): Promise<ProductModel> {
		return firstValueFrom(this.http.post<ProductModel>(`${this.baseUrl}`, product));
	}

	updateProduct(product: ProductModel): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}`, product));
	}

	deleteProduct(id: string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${id}`));
	}

	getVariant(id: string): Promise<ProductVariantModel> {
		return firstValueFrom(this.http.get<ProductVariantModel>(`${this.baseUrl}/variants/${id}`));
	}

	getVariantList(id: string): Promise<VariantListModel> {
		return firstValueFrom(this.http.get<VariantListModel>(`${this.baseUrl}/variant-lists/${id}`)).then(list => this.sortVariantListOptions(list));
	}

	getAllVariantLists(): Promise<VariantListModel[]> {
		return firstValueFrom(this.http.get<VariantListModel[]>(`${this.baseUrl}/variant-lists`)).then(variantLists => {
			variantLists.forEach(list => this.sortVariantListOptions(list));
			return variantLists.sort((a, b) => a.name.localeCompare(b.name));
		});
	}
	private numberAndTextCompare(a: string | number, b: string | number) {
		const numberA = Number(a), numberB = Number(b);
		if (!isNaN(numberA) && !isNaN(numberB)) return numberA - numberB;
		if (isNaN(numberA) && isNaN(numberB)) return (a as string).localeCompare(b as string);
		return isNaN(numberA) ? 1 : -1; // Always sort numbers before strings
	}
	private sortVariantListOptions(list: VariantListModel) {
		list.variantListOptions = list.variantListOptions.sort((a, b) => this.numberAndTextCompare(a.name, b.name));
		return list;
	}
	private getProductVariantsOptionsValue(variant: ProductVariantModel, variantList: VariantListModel, lists: VariantListModel[]): string {
		let variantListOption = lists.find(vl => vl.id == variantList.id)?.variantListOptions.find(vlo => variant.productVariantsOptions.map(pvo => pvo.variantOption!.id).indexOf(vlo.id) !== -1);
		return variantListOption ? variantListOption.name : '';
	}
	public sortVariants(variants: ProductVariantModel[], lists: VariantListModel[]) {
		for (const list of lists) {
			variants.sort((a, b) => {
				return this.numberAndTextCompare(this.getProductVariantsOptionsValue(a, list, lists), this.getProductVariantsOptionsValue(b, list, lists));
			});
		}
	}

	createOrUpdateVariantList(variantList: VariantListModel): Promise<VariantListModel> {
		if (variantList.id) return firstValueFrom(this.http.put<VariantListModel>(`${this.baseUrl}/variant-lists`, variantList)).then(list => this.sortVariantListOptions(list));
		else return firstValueFrom(this.http.post<VariantListModel>(`${this.baseUrl}/variant-lists`, variantList)).then(list => this.sortVariantListOptions(list));
	}

	generateVariantCombinations(optionsLists: VariantListOptionModel[][]) {
		const productVariants = new Array<ProductVariantModel>();

		const iterator = CartesianProduct.apply(this, optionsLists);
		const calculatedVariantCombinations = Array.from([...iterator]); //A list of our variant combinations.

		calculatedVariantCombinations.forEach((options: VariantListOptionModel[]) => {
			const productVariant = new ProductVariantModel();
			options.forEach((variantListOption: VariantListOptionModel) => {
				let productVariantOption = new ProductVariantsOptionModel();
				productVariantOption.variantOption = variantListOption;
				productVariantOption.variant = productVariant;
				productVariant.productVariantsOptions.push(productVariantOption);
			});
			productVariants.push(productVariant);
		});
		return productVariants;
	}

	getAllProductFilters(): Promise<ProductTagFilterModel[]> {
		return firstValueFrom(this.http.get<ProductTagFilterModel[]>(`${this.baseUrl}/filters`));
	}

	getAllProductTags(): Promise<TagModel[]> {
		return firstValueFrom(this.http.get<TagModel[]>(`${this.baseUrl}/tags`));
	}

	private activeProductsPromise?: Promise<ProductModel[]>;
	getAllActiveProducts(reloadCache: boolean = true): Promise<ProductModel[]> {
		if (reloadCache || !this.activeProductsPromise) this.activeProductsPromise = firstValueFrom(this.http.get<ProductModel[]>(`${this.baseUrl}`));
		return this.activeProductsPromise;
	}

	getAllProducts(): Promise<ProductModel[]> {
		const onlyActive = false;
		return firstValueFrom(this.http.get<ProductModel[]>(`${this.baseUrl}?onlyActive=${onlyActive}`));
	}

	getVariantName(variant?: ProductVariantModel, includeProductName: boolean = true, includePeriodLists: boolean = true): string {
		if (!variant || !variant.productVariantsOptions || variant.productVariantsOptions.filter(pvo => !pvo.variantOption?.variantList).length > 0) return '';
		if (includeProductName && !variant.product) return '';

		var variantOptionNames = variant.productVariantsOptions
			.map(o => o.variantOption!)
			.sort(o => o.variantList!.type == VariantListTypes.Period ? -1 : 1)
			.filter(o => includePeriodLists || o.variantList?.type != VariantListTypes.Period)
			.map(o => `${o.name} ${o.variantList?.name}`);

		var nameParts = [];
		if (includeProductName) nameParts.push(variant.product?.name);
		if (variantOptionNames.length > 0) nameParts.push(variantOptionNames.join(', '));
		return nameParts.join(' - ');
	}

	sortVariantsByPeriod(variants: ProductVariantModel[]) {
		return [...variants].sort((a, b) => {
			const periodA = parseInt(a.productVariantsOptions.map(pvo => pvo.variantOption).find(o => o?.variantList?.type == VariantListTypes.Period)?.name ?? '0');
			const periodB = parseInt(b.productVariantsOptions.map(pvo => pvo.variantOption).find(o => o?.variantList?.type == VariantListTypes.Period)?.name ?? '0');
			return periodA - periodB;
		});
	}

	getVariantPeriod(variant: ProductVariantModel): number | null {
		const periodOption = variant.productVariantsOptions.map(pvo => pvo.variantOption).find(o => o?.variantList?.type == VariantListTypes.Period);
		if (!periodOption) return null;
		const period = parseInt(periodOption.name);
		return isNaN(period) ? null : period;
	}
}
